import type { BrokerSetting, ISODate, MarginType } from "../types.js";
import type { MarketCalendar } from "../calendar/business-days.js";
import { diffDays } from "../calendar/date.js";
import { feeFor } from "./fee.js";

/**
 * Cost estimate for one cross trade. See docs/04-cost-calculation.md.
 * All money values are yen; rates are fractions (0.0115 = 1.15%).
 */

export interface CrossCostInput {
  price: number;
  shares: number;
  marginType: MarginType;
  /** 建て日 */
  openDate: ISODate;
  /** 現渡日 */
  closeDate: ISODate;
  /** 制度のみ。1株あたりの逆日歩の合計（日数込み）。undefined は 0 扱い */
  gyakuhibuPerShareTotal?: number;
  /** 1株あたりの予想配当。undefined は配当なし扱い */
  dividendPerShare?: number;
  dividendTaxRate: number;
  broker: BrokerSetting;
}

export interface CrossCostBreakdown {
  tradeAmount: number;
  fee: number;
  loanDays: number;
  loanRate: number;
  loanCost: number;
  gyakuhibuCost: number;
  dividendPlus: number;
  total: number;
}

export function loanRateFor(broker: BrokerSetting, marginType: MarginType): number {
  switch (marginType) {
    case "seido":
      return broker.loanRateSeido;
    case "ippan_short":
      return broker.loanRateIppanShort;
    case "ippan_long":
      return broker.loanRateIppanLong;
  }
}

export function dividendAdjRateFor(broker: BrokerSetting, marginType: MarginType): number {
  return marginType === "seido" ? broker.dividendAdjRateSeido : broker.dividendAdjRateIppan;
}

/** Calendar days between the settlement dates of the open and close trades. */
export function loanDays(cal: MarketCalendar, openDate: ISODate, closeDate: ISODate): number {
  return Math.max(0, diffDays(cal.settlementDate(openDate), cal.settlementDate(closeDate)));
}

/**
 * 配当プラス分 (制度信用). Returns 0 when the broker setting disables it or
 * the margin type is not 制度.
 */
export function dividendPlus(
  dividendTotal: number,
  taxRate: number,
  adjRate: number,
): number {
  const received = dividendTotal * (1 - taxRate);
  const paid = dividendTotal * adjRate;
  const refund = paid * taxRate;
  return received - paid + refund;
}

export function estimateCrossCost(cal: MarketCalendar, input: CrossCostInput): CrossCostBreakdown {
  const { broker, marginType } = input;
  const tradeAmount = input.price * input.shares;

  const fee =
    feeFor(broker.feeBuy, tradeAmount) +
    feeFor(broker.feeSellMargin, tradeAmount) +
    feeFor(broker.feeGenwatashi, tradeAmount);

  const days = loanDays(cal, input.openDate, input.closeDate);
  const rate = loanRateFor(broker, marginType);
  const loanCost = Math.round((tradeAmount * rate * days) / 365);

  const gyakuhibuCost =
    marginType === "seido" ? Math.round((input.gyakuhibuPerShareTotal ?? 0) * input.shares) : 0;

  let plus = 0;
  if (marginType === "seido" && broker.enableDividendPlus && input.dividendPerShare) {
    plus = Math.round(
      dividendPlus(
        input.dividendPerShare * input.shares,
        input.dividendTaxRate,
        dividendAdjRateFor(broker, marginType),
      ),
    );
  }

  return {
    tradeAmount,
    fee,
    loanDays: days,
    loanRate: rate,
    loanCost,
    gyakuhibuCost,
    dividendPlus: plus,
    total: fee + loanCost + gyakuhibuCost - plus,
  };
}
